import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Interfaces para los datos
export interface ProductoData {
  sku: string;
  stock: number;
  precioVenta: number;
  marca: string;
  genero: string;
  temporada: string;
  [key: string]: any; // Campos personalizados dinámicos
}

export interface CategoriaData {
  id: string;
  name: string;
  productos: ProductoData[];
  isEmpty: boolean;
}

export interface ProductoConImagen extends ProductoData {
  imagenUrl?: string;
  imagenPath?: string;
}

// Campos que se excluyen globalmente
const EXCLUDED_FIELDS = ['precioCompra', 'fechaIngreso', 'proveedor'];

// Hook para obtener datos de stock desde Firestore y Storage
export const useFetchStockData = () => {
  const { user } = useAuth();
  const [categorias, setCategorias] = useState<CategoriaData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Función para excluir campos no deseados
  const excludeFields = useCallback((obj: any): any => {
    const filtered: any = {};
    Object.keys(obj).forEach(key => {
      if (!EXCLUDED_FIELDS.includes(key)) {
        filtered[key] = obj[key];
      }
    });
    return filtered;
  }, []);

  // Función para obtener URL de imagen desde Storage
  const getImageUrlFromStorage = useCallback(async (imagePath: string): Promise<string | null> => {
    if (!user || !window.auth?.storage) {
      console.warn('Firebase Storage no está disponible');
      return null;
    }

    try {
      const storage = window.auth.storage;
      const imageRef = window.auth.ref(storage, imagePath);
      const url = await window.auth.getDownloadURL(imageRef);
      return url;
    } catch (error) {
      console.warn('Error obteniendo URL de imagen:', error);
      return null;
    }
  }, [user]);

  // Función para obtener todas las categorías del usuario
  const fetchUserCategories = useCallback(async (): Promise<string[]> => {
    // Lista completa de categorías que siempre deben mostrarse (con mayúscula)
    const allCategories = [
      'Buzo',
      'Calzado', 
      'Camisa',
      'Campera',
      'Pantalón',
      'Pañuelo',
      'Pollera',
      'Vestido',
      'Remera',
      'Sombrero, Gorra, Gorro'
    ];

    if (!user || !window.auth?.storage) {
      console.log('⚠️ Firebase Storage no disponible, usando categorías por defecto');
      return allCategories;
    }

    try {
      const storage = window.auth.storage;
      const listRef = window.auth.ref(storage, `users/${user.uid}/categories`);
      
      console.log('🔍 Buscando categorías en:', listRef.fullPath);
      const res = await window.auth.listAll(listRef);
      
      const foundCategoryNames = res.prefixes.map((prefix: any) => prefix.name);
      console.log('📂 Categorías encontradas en Storage:', foundCategoryNames);
      
      // Filtrar categorías para evitar duplicados con diferentes capitalizaciones
      const filteredFoundCategories = foundCategoryNames.filter((foundName: string) => {
        // Si la categoría encontrada está en minúscula, verificar si ya existe en mayúscula
        const foundNameLower = foundName.toLowerCase();
        const foundNameTitle = foundName.charAt(0).toUpperCase() + foundName.slice(1).toLowerCase();
        
        // Casos especiales
        if (foundNameLower === 'sgg') {
          return false; // SGG se maneja como "Sombrero, Gorra, Gorro"
        }
        
        // Si ya existe una versión con mayúscula en allCategories, no incluir la minúscula
        const hasUppercaseVersion = allCategories.some(cat => 
          cat.toLowerCase() === foundNameLower || 
          cat.toLowerCase() === foundNameTitle.toLowerCase()
        );
        
        if (hasUppercaseVersion) {
          console.log(`📂 Filtrando categoría duplicada: ${foundName} (ya existe versión con mayúscula)`);
          return false;
        }
        
        return true;
      });
      
      console.log('📂 Categorías filtradas:', filteredFoundCategories);
      
      // Combinar categorías por defecto con las categorías encontradas (ya filtradas)
      const combinedCategories = Array.from(new Set([...allCategories, ...filteredFoundCategories]));
      console.log('📂 Categorías finales:', combinedCategories);
      
      return combinedCategories;
    } catch (error) {
      console.error('❌ Error obteniendo categorías, usando categorías por defecto:', error);
      return allCategories;
    }
  }, [user]);

  // Función para obtener productos de una categoría específica
  const fetchStockByCategory = useCallback(async (userId: string, categoryName: string): Promise<ProductoConImagen[]> => {
    console.log(`🔍 [DEBUG] Iniciando búsqueda de productos para categoría: ${categoryName}`);
    
    if (!window.auth?.db) {
      console.error('❌ [DEBUG] Firestore no está disponible');
      throw new Error('Firestore no está disponible');
    }

    try {
      const db = window.auth.db;
      const productos: ProductoConImagen[] = [];
      
      // Buscar productos en la categoría con mayúscula (nombre oficial)
      const productsRefOfficial = window.auth.collection(db, `users/${userId}/categories/${categoryName}/stock`);
      console.log('🔍 [DEBUG] Buscando productos en categoría oficial:', `users/${userId}/categories/${categoryName}/stock`);
      
      try {
        const snapshotOfficial = await window.auth.getDocs(productsRefOfficial);
        console.log('🔍 [DEBUG] Snapshot oficial obtenido:', snapshotOfficial.docs.length, 'documentos');
        
        for (const doc of snapshotOfficial.docs) {
          console.log('🔍 [DEBUG] Procesando documento oficial:', doc.id, doc.data());
          const productData = doc.data();
          const filteredData = excludeFields(productData);
          
          const producto: ProductoConImagen = {
            sku: filteredData.sku || doc.id,
            stock: filteredData.stock || 0,
            precioVenta: filteredData.precioVenta || 0,
            marca: filteredData.marca || '',
            genero: filteredData.genero || '',
            temporada: filteredData.temporada || '',
            ...filteredData
          };

          const imagePath = `users/${userId}/categories/${categoryName}/stock/${doc.id}/imagen`;
          const imageUrl = await getImageUrlFromStorage(imagePath);
          
          if (imageUrl) {
            producto.imagenUrl = imageUrl;
            producto.imagenPath = imagePath;
          }

          productos.push(producto);
        }
      } catch (officialError) {
        console.log('🔍 [DEBUG] No se encontraron productos en categoría oficial:', officialError instanceof Error ? officialError.message : String(officialError));
      }
      
      // Buscar productos en la categoría con minúscula (si existe)
      const categoryNameLower = categoryName.toLowerCase();
      if (categoryNameLower !== categoryName) {
        const productsRefLower = window.auth.collection(db, `users/${userId}/categories/${categoryNameLower}/stock`);
        console.log('🔍 [DEBUG] Buscando productos en categoría minúscula:', `users/${userId}/categories/${categoryNameLower}/stock`);
        
        try {
          const snapshotLower = await window.auth.getDocs(productsRefLower);
          console.log('🔍 [DEBUG] Snapshot minúscula obtenido:', snapshotLower.docs.length, 'documentos');
          
          for (const doc of snapshotLower.docs) {
            console.log('🔍 [DEBUG] Procesando documento minúscula:', doc.id, doc.data());
            const productData = doc.data();
            const filteredData = excludeFields(productData);
            
            const producto: ProductoConImagen = {
              sku: filteredData.sku || doc.id,
              stock: filteredData.stock || 0,
              precioVenta: filteredData.precioVenta || 0,
              marca: filteredData.marca || '',
              genero: filteredData.genero || '',
              temporada: filteredData.temporada || '',
              ...filteredData
            };

            // Intentar obtener imagen desde ambas rutas posibles
            const imagePathOfficial = `users/${userId}/categories/${categoryName}/stock/${doc.id}/imagen`;
            const imagePathLower = `users/${userId}/categories/${categoryNameLower}/stock/${doc.id}/imagen`;
            
            let imageUrl = await getImageUrlFromStorage(imagePathOfficial);
            if (!imageUrl) {
              imageUrl = await getImageUrlFromStorage(imagePathLower);
            }
            
            if (imageUrl) {
              producto.imagenUrl = imageUrl;
              producto.imagenPath = imageUrl.includes(categoryNameLower) ? imagePathLower : imagePathOfficial;
            }

            productos.push(producto);
          }
        } catch (lowerError) {
          console.log('🔍 [DEBUG] No se encontraron productos en categoría minúscula:', lowerError instanceof Error ? lowerError.message : String(lowerError));
        }
      }
      
      // Caso especial para "Sombrero, Gorra, Gorro" que puede estar como "sgg"
      if (categoryName === 'Sombrero, Gorra, Gorro') {
        const productsRefSGG = window.auth.collection(db, `users/${userId}/categories/sgg/stock`);
        console.log('🔍 [DEBUG] Buscando productos en categoría SGG:', `users/${userId}/categories/sgg/stock`);
        
        try {
          const snapshotSGG = await window.auth.getDocs(productsRefSGG);
          console.log('🔍 [DEBUG] Snapshot SGG obtenido:', snapshotSGG.docs.length, 'documentos');
          
          for (const doc of snapshotSGG.docs) {
            console.log('🔍 [DEBUG] Procesando documento SGG:', doc.id, doc.data());
            const productData = doc.data();
            const filteredData = excludeFields(productData);
            
            const producto: ProductoConImagen = {
              sku: filteredData.sku || doc.id,
              stock: filteredData.stock || 0,
              precioVenta: filteredData.precioVenta || 0,
              marca: filteredData.marca || '',
              genero: filteredData.genero || '',
              temporada: filteredData.temporada || '',
              ...filteredData
            };

            const imagePathSGG = `users/${userId}/categories/sgg/stock/${doc.id}/imagen`;
            const imageUrl = await getImageUrlFromStorage(imagePathSGG);
            
            if (imageUrl) {
              producto.imagenUrl = imageUrl;
              producto.imagenPath = imagePathSGG;
            }

            productos.push(producto);
          }
        } catch (sggError) {
          console.log('🔍 [DEBUG] No se encontraron productos en categoría SGG:', sggError instanceof Error ? sggError.message : String(sggError));
        }
      }
      
      console.log(`✅ [DEBUG] ${productos.length} productos encontrados en categoría ${categoryName}:`, productos);
      return productos;
    } catch (error) {
      console.error(`❌ [DEBUG] Error obteniendo productos de categoría ${categoryName}:`, error);
      throw error;
    }
  }, [excludeFields, getImageUrlFromStorage]);

  // Función principal para cargar todos los datos
  const loadStockData = useCallback(async () => {
    console.log('🔄 [DEBUG] Iniciando loadStockData...');
    
    if (!user) {
      console.error('❌ [DEBUG] Usuario no autenticado');
      setError('Usuario no autenticado');
      return;
    }

    console.log('🔄 [DEBUG] Usuario autenticado:', user.uid);

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 [DEBUG] Iniciando carga de datos de stock...');
      
      // Obtener todas las categorías
      const categoryNames = await fetchUserCategories();
      console.log('🔄 [DEBUG] Categorías obtenidas:', categoryNames);
      
      const categoriasData: CategoriaData[] = [];
      
      // Para cada categoría, obtener sus productos
      for (const categoryName of categoryNames) {
        console.log(`🔄 [DEBUG] Procesando categoría: ${categoryName}`);
        try {
          const productos = await fetchStockByCategory(user.uid, categoryName);
          console.log(`🔄 [DEBUG] Productos obtenidos para ${categoryName}:`, productos);
          
          categoriasData.push({
            id: categoryName,
            name: categoryName,
            productos,
            isEmpty: productos.length === 0
          });
        } catch (categoryError) {
          console.warn(`⚠️ [DEBUG] Error cargando categoría ${categoryName}:`, categoryError);
          // Agregar categoría vacía si hay error
          categoriasData.push({
            id: categoryName,
            name: categoryName,
            productos: [],
            isEmpty: true
          });
        }
      }
      
      console.log('🔄 [DEBUG] Datos finales de categorías:', categoriasData);
      setCategorias(categoriasData);
      console.log('✅ [DEBUG] Datos de stock cargados exitosamente:', categoriasData.length, 'categorías');
      
    } catch (error) {
      console.error('❌ [DEBUG] Error cargando datos de stock:', error);
      setError('Error cargando datos de stock');
    } finally {
      setLoading(false);
    }
  }, [user, fetchUserCategories, fetchStockByCategory]);

  // Función para actualizar datos (sin caché)
  const updateOnModalOpen = useCallback(() => {
    console.log('🔄 Actualizando datos al abrir modal...');
    loadStockData();
  }, [loadStockData]);

  // Función para obtener productos de una categoría específica
  const getProductsByCategory = useCallback((categoryName: string): ProductoConImagen[] => {
    const categoria = categorias.find(cat => cat.name === categoryName);
    return categoria ? categoria.productos : [];
  }, [categorias]);

  // Función para obtener todas las categorías (incluso vacías)
  const getAllCategories = useCallback((): CategoriaData[] => {
    return categorias;
  }, [categorias]);

  // Función para verificar si una categoría está vacía
  const isCategoryEmpty = useCallback((categoryName: string): boolean => {
    const categoria = categorias.find(cat => cat.name === categoryName);
    return categoria ? categoria.isEmpty : true;
  }, [categorias]);

  // Cargar datos automáticamente cuando el usuario cambia
  useEffect(() => {
    if (user) {
      loadStockData();
    }
  }, [user, loadStockData]);

  return {
    categorias,
    loading,
    error,
    loadStockData,
    updateOnModalOpen,
    getProductsByCategory,
    getAllCategories,
    isCategoryEmpty,
    excludeFields,
    getImageUrlFromStorage
  };
};
